import type { CoordinatePair, ExtractedRecord } from "@/types";
import type { PdfHighlight } from "@/components/common/PdfViewer";

/**
 * Convert CoordinatePair[] (backend int values = inches × 100) into [x, y] inch
 * pairs. The highlight layer converts inches → canvas px via `inches × 72 ×
 * scale`, so this is orientation-independent (portrait, landscape, any size).
 */
export const coordinatesToInches = (coords: CoordinatePair[]): [number, number][] =>
  coords.map(({ x, y }) => [x / 100, y / 100]);

/**
 * Build PdfHighlight[] from an ExtractedRecord's fields that carry bounding-box
 * coordinates. Returns [] when the record has no placeable coordinate data.
 *
 * A highlight needs BOTH coordinates and a known `page_number`. In the backend
 * extraction these are independent metadata keys (`__metadata_page_number` per
 * item vs `__metadata_coordinates_<key>` per field — see transformer.go), so an
 * absent page_number means we genuinely don't know which page the box is on.
 * We skip the highlight in that case rather than assume page 1, which would
 * mis-place a page-2+ field onto page 1 on a multi-page document. (PR 4365 review.)
 */
export const buildPdfHighlightsForRecord = (record: ExtractedRecord | null): PdfHighlight[] => {
  if (!record?.extracted_fields) return [];

  return record.extracted_fields
    .filter((f) => f.source?.coordinates?.length && f.source.page_number)
    .map((f) => ({
      fieldName: f.target_field,
      value: String(f.extracted_value ?? ""),
      status: "extracted" as const,
      metadata: {
        pageNumber: f.source.page_number!,
        boundingBox: coordinatesToInches(f.source.coordinates!),
        textSpan: String(f.extracted_value ?? ""),
      },
    }));
};
